import { useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Select, Field, Empty, StatusBadge, Btn, Input } from "@/components/kit";
import { CalendarDays, ChevronLeft, ChevronRight, Clock, GripVertical, Inbox, AlertTriangle, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import PostEditor from "@/components/PostEditor";
import IdeaDropModal from "@/components/IdeaDropModal";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function startOfWeek(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = (date.getDay() + 6) % 7; // 0 = Monday
  date.setDate(date.getDate() - day);
  return date;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtDay(d) {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
function fmtTime(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function Calendario() {
  const { activeOrgId } = useAuth();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [items, setItems] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [draggingId, setDraggingId] = useState(null);
  const [drop, setDrop] = useState(null); // { itemId, date }
  const [time, setTime] = useState("09:00");
  const [dateStr, setDateStr] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [dragIdea, setDragIdea] = useState(null);
  const [ideaDrop, setIdeaDrop] = useState(null); // { idea, date }
  const [sideTab, setSideTab] = useState("backlog");

  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/brands").then((r) => { setBrands(r.data); setBrandId(""); });
  }, [activeOrgId]);

  const loadItems = () => {
    const url = brandId ? `/content?brand_id=${brandId}` : "/content";
    client.get(url).then((r) => setItems(r.data));
  };
  const loadIdeas = () => client.get("/ideas", { params: brandId ? { brand_id: brandId } : {} })
    .then((r) => setIdeas(r.data.filter((i) => i.status === "NEW" && i.brand_id)));
  useEffect(() => { if (activeOrgId) { loadItems(); loadIdeas(); } /* eslint-disable-next-line */ }, [activeOrgId, brandId]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }),
    [weekStart]
  );

  const effectiveDate = (i) => i.scheduled_at || (i.status === "PUBLISHED" ? i.published_at : null);
  const backlog = items.filter((i) => !effectiveDate(i));
  const scheduled = items.filter((i) => effectiveDate(i));
  const today = new Date();

  const toDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const openSchedule = (item, date) => {
    const base = date || (item.scheduled_at ? new Date(item.scheduled_at) : new Date());
    setDrop({ itemId: item.id });
    setDateStr(toDateInput(base));
    setTime(item.scheduled_at ? fmtTime(item.scheduled_at) : "09:00");
    setDraggingId(null);
  };

  const onDropDay = (date) => {
    if (dragIdea) { setIdeaDrop({ idea: dragIdea, date }); setDragIdea(null); return; }
    if (!draggingId) return;
    const item = items.find((i) => i.id === draggingId);
    if (item) openSchedule(item, date);
    else setDraggingId(null);
  };

  const confirmSchedule = async () => {
    if (!drop) return;
    setSaving(true);
    try {
      const [hh, mm] = time.split(":").map(Number);
      const [y, mo, da] = dateStr.split("-").map(Number);
      const dt = new Date(y, mo - 1, da, Number.isFinite(hh) ? hh : 9, Number.isFinite(mm) ? mm : 0, 0, 0);
      const { data } = await client.patch(`/content/${drop.itemId}`, {
        status: "SCHEDULED", scheduled_at: dt.toISOString(),
      });
      setItems((prev) => prev.map((i) => (i.id === drop.itemId ? { ...i, ...data } : i)));
      toast.success(`Agendado para ${fmtDay(dt)} às ${time}`);
      setDrop(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível agendar. Verifique suas permissões.");
    } finally {
      setSaving(false);
    }
  };

  const channelDot = (ch) =>
    ch === "Instagram" ? "bg-gradient-to-tr from-amber-400 via-rose-500 to-fuchsia-600"
      : ch === "Facebook" ? "bg-blue-600"
      : ch === "WhatsApp" ? "bg-emerald-500"
      : "bg-slate-400";

  const DraggableCard = ({ p }) => (
    <div
      draggable
      onDragStart={() => setDraggingId(p.id)}
      onDragEnd={() => setDraggingId(null)}
      onClick={() => setEditing(p)}
      title="Clique para editar o conteúdo"
      data-testid={`cal-item-${p.id}`}
      className={`group mb-2 overflow-hidden cursor-pointer rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-[#3457d5]/50 hover:shadow-md ${draggingId === p.id ? "opacity-40" : ""}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={`h-2 w-2 shrink-0 rounded-full ${channelDot(p.channel)}`} />
          <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500">{p.channel}</span>
        </span>
        <button
          type="button"
          title="Agendar / alterar horário"
          data-testid={`cal-schedule-${p.id}`}
          onClick={(e) => { e.stopPropagation(); openSchedule(p); }}
          className="inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 transition hover:bg-[#3457d5]/10 hover:text-[#3457d5]"
        >
          <Clock size={11} /> {effectiveDate(p) ? fmtTime(effectiveDate(p)) : "agendar"}
        </button>
      </div>
      <div className="mt-1.5 line-clamp-2 text-xs font-medium leading-snug text-slate-800">{p.title || p.theme}</div>
      <div className="mt-2 flex items-center justify-between">
        {p.publish_failed
          ? <span data-testid={`cal-failed-${p.id}`} title={p.publish_error} className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700"><AlertTriangle size={11} className="shrink-0" /> Falhou</span>
          : <StatusBadge status={p.status} />}
        <GripVertical size={13} className="shrink-0 text-slate-300 transition group-hover:text-[#3457d5]" />
      </div>
      {p.publish_failed && p.publish_error && (
        <div data-testid={`cal-failed-msg-${p.id}`} className="mt-1.5 rounded-md border border-rose-200 bg-rose-50 px-1.5 py-1 text-[10px] font-medium leading-snug text-rose-700 line-clamp-3">{p.publish_error}</div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Calendário Editorial"
        subtitle="Arraste para reagendar, clique no card para editar o conteúdo, ou toque no horário (⏰) para mudar a data/hora."
      />

      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-xs">
          <Field label="Filtrar por marca">
            <Select data-testid="calendar-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">Todas as marcas</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </Field>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="secondary" data-testid="cal-prev-week" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>
            <ChevronLeft size={16} />
          </Btn>
          <Btn variant="secondary" data-testid="cal-today" onClick={() => setWeekStart(startOfWeek(new Date()))}>Hoje</Btn>
          <Btn variant="secondary" data-testid="cal-next-week" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
            <ChevronRight size={16} />
          </Btn>
          <span className="ml-1 text-sm font-semibold text-slate-600">
            {fmtDay(days[0])} — {fmtDay(days[6])}
          </span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        {/* Backlog / Ideias */}
        <Card className="!p-4">
          <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 text-[11px] font-bold uppercase tracking-wide">
            <button data-testid="cal-tab-backlog" onClick={() => setSideTab("backlog")} className={`flex items-center justify-center gap-1 rounded-md py-1.5 transition-colors ${sideTab === "backlog" ? "bg-white text-slate-700 shadow-sm" : "text-slate-400"}`}><Inbox size={12} /> Posts ({backlog.length})</button>
            <button data-testid="cal-tab-ideas" onClick={() => setSideTab("ideas")} className={`flex items-center justify-center gap-1 rounded-md py-1.5 transition-colors ${sideTab === "ideas" ? "bg-white text-slate-700 shadow-sm" : "text-slate-400"}`}><Lightbulb size={12} /> Ideias ({ideas.length})</button>
          </div>
          {sideTab === "backlog" ? (
            <div className="max-h-[560px] overflow-y-auto pr-1" data-testid="cal-backlog">
              {backlog.length ? backlog.map((p) => <DraggableCard key={p.id} p={p} compact />)
                : <div className="rounded-lg border-2 border-dashed border-slate-200 py-8 text-center text-xs text-slate-400">Tudo agendado</div>}
            </div>
          ) : (
            <div className="max-h-[560px] overflow-y-auto pr-1" data-testid="cal-ideas">
              <p className="mb-2 text-[11px] text-slate-400">Arraste uma ideia para um dia para criar os posts.</p>
              {ideas.length ? ideas.map((i) => (
                <div key={i.id} draggable data-testid={`cal-idea-${i.id}`}
                  onDragStart={() => setDragIdea(i)} onDragEnd={() => setTimeout(() => setDragIdea(null), 0)}
                  className={`mb-2 cursor-grab rounded-xl border border-amber-200 bg-amber-50/60 p-2.5 text-xs font-medium text-slate-700 transition-transform duration-150 hover:-translate-y-0.5 ${dragIdea?.id === i.id ? "opacity-40" : ""}`}>
                  <div className="flex items-start gap-1.5"><Lightbulb size={12} className="mt-0.5 shrink-0 text-amber-500" /><span className="line-clamp-3">{i.text}</span></div>
                  {i.pillar && <div className="mt-1 text-[10px] text-slate-400">{i.pillar}</div>}
                </div>
              )) : <div className="rounded-lg border-2 border-dashed border-slate-200 py-8 text-center text-xs text-slate-400">Nenhuma ideia nova. Use “Sugerir temas com IA” na Caixa de Ideias.</div>}
            </div>
          )}
        </Card>

        {/* Week grid */}
        <Card className="overflow-x-auto !p-4">
          <div className="grid min-w-[720px] grid-cols-7 gap-2">
            {days.map((d) => {
              const isToday = sameDay(d, today);
              const dayItems = scheduled
                .filter((x) => sameDay(new Date(effectiveDate(x)), d))
                .sort((a, b) => new Date(effectiveDate(a)) - new Date(effectiveDate(b)));
              return (
                <div
                  key={d.toISOString()}
                  data-testid={`cal-col-${d.toISOString().slice(0, 10)}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropDay(d)}
                  className={`min-h-[420px] rounded-xl border p-2 transition ${isToday ? "border-[#3457d5] bg-[#3457d5]/[0.04]" : "border-slate-200"} ${draggingId || dragIdea ? "outline-dashed outline-2 outline-offset-[-4px] outline-[#3457d5]/30" : ""}`}
                >
                  <div className={`mb-2 flex items-center justify-between text-xs font-bold ${isToday ? "text-[#3457d5]" : "text-slate-400"}`}>
                    <span className="flex items-center gap-1"><CalendarDays size={12} /> {WEEKDAYS[(d.getDay() + 6) % 7]}</span>
                    <span>{d.getDate()}</span>
                  </div>
                  {dayItems.map((p) => <DraggableCard key={p.id} p={p} />)}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {!items.length && <div className="mt-4"><Empty>Nenhum conteúdo. Gere uma campanha primeiro.</Empty></div>}

      {/* Time picker modal */}
      {drop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="cal-time-modal" onClick={() => !saving && setDrop(null)}>
          <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-extrabold text-slate-900">Agendar publicação</h3>
            <p className="mt-1 text-sm text-slate-500">Escolha a data e o horário da publicação.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label="Data">
                <Input type="date" value={dateStr} data-testid="cal-date-input" onChange={(e) => setDateStr(e.target.value)} />
              </Field>
              <Field label="Horário">
                <Input type="time" value={time} data-testid="cal-time-input" onChange={(e) => setTime(e.target.value)} />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="ghost" data-testid="cal-time-cancel" disabled={saving} onClick={() => setDrop(null)}>Cancelar</Btn>
              <Btn data-testid="cal-time-confirm" disabled={saving} onClick={confirmSchedule}>
                {saving ? "Agendando..." : "Confirmar"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {ideaDrop && <IdeaDropModal idea={ideaDrop.idea} date={ideaDrop.date} onClose={() => setIdeaDrop(null)}
        onDone={() => { setIdeaDrop(null); loadItems(); loadIdeas(); }} />}
      {editing && <PostEditor item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadItems(); }} />}
    </div>
  );
}
